import { useEffect, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { Link } from "@/lib/router-compat";
import { Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslations } from "@/hooks/useTranslations";
import { useCookieConsent } from "@/contexts/CookieConsentContext";
import { trackGAEvent } from "@/lib/analytics";

const SESSION_KEY = "agape.personafiPopup.seen";
const DELAY_MS = 30_000;
const SCROLL_THRESHOLD = 0.5;

/**
 * Site-wide promo modal for PersonaFi. Appears once per session after 30s or
 * once the visitor scrolls past 50% of the page — whichever comes first.
 * Hidden on the PersonaFi pages themselves and while the cookie banner is open.
 */
export function PersonaFiPopup() {
  const { language } = useLanguage();
  const t = useTranslations();
  const copy = t.personaFiPopup;
  const { hasDecided } = useCookieConsent();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(true);

  const isPersonaFiPage = /^\/(?:pl\/|by\/)?personaFi(?:\/|$)/i.test(pathname);
  const prefix = language === "pl" ? "/pl" : language === "by" ? "/by" : "";
  const personaFiHref = `${prefix}/personaFi`;

  useEffect(() => {
    try {
      setDone(sessionStorage.getItem(SESSION_KEY) === "1");
    } catch {
      setDone(false);
    }
  }, []);

  useEffect(() => {
    if (done || open || isPersonaFiPage || !hasDecided) return;

    let cancelled = false;
    const trigger = () => {
      if (cancelled) return;
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
      setOpen(true);
      trackGAEvent("personafi_popup_view", { language, page_path: pathname });
    };

    const onScroll = () => {
      const docH = document.documentElement.scrollHeight - window.innerHeight;
      if (docH > 0 && window.scrollY / docH >= SCROLL_THRESHOLD) trigger();
    };

    const timer = window.setTimeout(trigger, DELAY_MS);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener("scroll", onScroll);
    };
  }, [done, open, isPersonaFiPage, hasDecided, language, pathname]);

  const markSeen = () => {
    setDone(true);
    setOpen(false);
    try {
      sessionStorage.setItem(SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  const handleClose = () => {
    trackGAEvent("personafi_popup_dismiss", { language, page_path: pathname });
    markSeen();
  };

  const handleCta = () => {
    trackGAEvent("personafi_popup_cta_click", { language, page_path: pathname });
    markSeen();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-background/80 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={copy.title}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4 duration-300"
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label={copy.dismiss}
          className="absolute right-3 top-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
          <Sparkles className="w-3.5 h-3.5" />
          {copy.badge}
        </span>

        <h2 className="mt-3 text-xl font-bold text-foreground leading-snug pr-6">{copy.title}</h2>
        <p className="mt-2 text-sm text-foreground/75">{copy.description}</p>

        <div className="mt-5 flex flex-col sm:flex-row-reverse sm:items-center gap-3">
          <Button
            asChild
            size="lg"
            className="bg-gradient-to-r from-primary to-accent text-primary-foreground font-semibold hover:opacity-90 shadow-lg shadow-primary/20"
          >
            <Link to={personaFiHref} onClick={handleCta}>
              {copy.cta}
            </Link>
          </Button>
          <button
            type="button"
            onClick={handleClose}
            className="text-xs text-foreground/70 hover:text-foreground underline underline-offset-2 transition-colors sm:mr-auto"
          >
            {copy.later}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PersonaFiPopup;
